/**
 * Shared pieces of User Management's people-first tabs (ops mock version 13, §B4; Jeff, 28 October 2026) — the
 * loading state, the action icon coloured by meaning with its own hover tooltip, the eye that opens a person's access
 * panel, and the zone fan-out every list reads through.
 *
 * Copy-forked from the Grading page's `grading/shared.tsx` (the common-catalogue model: copy, never import across a
 * stream), so the two pages can diverge without either breaking the other. Colours live in `people-tabs.css`, scoped
 * to `.nx-users`.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Tooltip } from '../../../components/Tooltip';
import { useToast, toastFromError } from '../../../components/ToastProvider';
import { fanOutZones, failedZoneLabels, throwIfAllZonesFailed, type PortalZone, type ZoneResult } from '../../../lib/zones';
import { mergeZoneRows, type Zoned } from '../../../lib/zone-board';
import { formatDate } from '../../../lib/time-format';

/** Opens the person-centric access panel (ADR-113 D25) — the only place access is changed or removed. */
export type OpenPerson = (cognitoSub: string, zoneCode: string) => void;

/** The layered loading indicator — a skeleton AND a status line, in tandem (the standing directive). */
export function Loading({ text }: { text: string }) {
  return (
    <div role="status" aria-live="polite" data-testid="users-tab-loading">
      <div className="portal-orphans-skeleton" aria-hidden="true"><span /><span /><span /></div>
      <p className="panel-help" style={{ margin: '.5rem 0 0' }}>{text}</p>
    </div>
  );
}

const ICONS = {
  view: <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>,
  pause: <path d="M9.5 5.5v13M14.5 5.5v13" />,
  resume: <polygon points="7 4.5 19 12 7 19.5 7 4.5" />,
  assign: <><circle cx="9" cy="7" r="3.2" /><path d="M3.5 20a6 6 0 0111 0" /><path d="M15 12l2 2 4-4" /></>,
  detach: <><circle cx="9" cy="7" r="3.2" /><path d="M3.5 20a6 6 0 0111 0" /><path d="M15.5 11.5h6" /></>,
  review: <><path d="M4 20h4L19 9l-4-4L4 16z" /><path d="M13.5 6.5l4 4" /></>,
} satisfies Record<string, ReactNode>;

/**
 * An action button in an Actions column: the ops icon button, coloured by meaning through its `data-act` (view
 * neutral · assign / review blue · resume teal · pause / detach amber), with its own hover tooltip naming the action.
 */
export function ActionIcon({ act, icon, label, tip, onClick, href, disabled }: {
  act: string; icon: keyof typeof ICONS; label: string; tip: string; onClick?: () => void; href?: string; disabled?: boolean;
}) {
  const svg = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{ICONS[icon]}</svg>
  );
  return (
    <Tooltip content={tip} aria={tip}>
      {href
        ? <Link to={href} className="portal-action-icon-btn" data-act={act} aria-label={label}>{svg}</Link>
        : <button type="button" className="portal-action-icon-btn" data-act={act} aria-label={label} onClick={onClick} disabled={disabled}>{svg}</button>}
    </Tooltip>
  );
}

/** The eye on every row — opens the person's access panel. */
export function PersonEye({ name, onOpen, disabledTip }: { name: string; onOpen?: () => void; disabledTip?: string }) {
  return (
    <ActionIcon
      act="view"
      icon="view"
      label={`View ${name}'s access`}
      tip={disabledTip ?? "View this person's access — role changes, removal and reinstatement happen there"}
      onClick={onOpen}
      disabled={!onOpen}
    />
  );
}

/**
 * A list read fanned out across every configured zone, each row tagged with the zone it came from (ADR-122 Pattern 1).
 * One unreachable zone is a labelled partial read (`failedZones`); every zone failing is a failed read, toasted, with
 * Retry.
 */
export function useZoneList<T extends object>(read: (z: PortalZone) => Promise<T[]>, what: string) {
  const toast = useToast();
  const [rows, setRows] = useState<Zoned<T>[] | null>(null);
  const [results, setResults] = useState<ZoneResult<T>[] | null>(null);
  const [failed, setFailed] = useState(false);
  const load = useCallback(async () => {
    setFailed(false);
    try {
      const res = await fanOutZones(read);
      throwIfAllZonesFailed(res);
      setResults(res);
      setRows(mergeZoneRows(res));
    } catch (err) {
      setFailed(true);
      setRows([]);
      const { title, detail } = toastFromError(err, `Could not load ${what}`);
      toast.push({ variant: 'error', title, detail });
    }
    // `read` and `what` are module-level per tab; the list loads once and reloads on demand.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { void load(); }, [load]);
  // A zone that did not answer is named on the page (ZoneFanoutWarning), not toasted — the
  // User Management page's own pattern, so the gap stays visible while the list is read.
  const failedZones = results ? failedZoneLabels(results) : [];
  return { rows, results, failed, failedZones, reload: load };
}

/** The load-failure line every list carries, with Retry (the sibling tabs' pattern; the detail went to a toast). */
export function LoadFailed({ what, onRetry }: { what: string; onRetry: () => void }) {
  return (
    <div className="portal-error" role="alert">
      Could not load {what}.{' '}
      <button type="button" className="portal-link-button" onClick={onRetry}>Retry</button>
    </div>
  );
}

/** A day as the console writes it ("29 Oct 2026", the viewer's zone) — the one formatter, lib/time-format.tsx. */
export const fmtDate = (iso: string | null | undefined): string => (iso ? formatDate(iso, 'medium') : '—');
